import { Constants } from '../../internal/Constants';
import Styles from './Button.module.css';
import { KeyboardEvent, Ref, useCallback, useMemo } from 'react';

/** Props for the Button component. */
interface IButtonDTO {
  text: string;
  title?: string;
  disabled?: boolean;
  rtl?: boolean;
  // Native button type. Defaults to undefined so a button inside a form still acts as the form's submit. Pass 'button' for secondary actions that must NOT submit the enclosing form.
  type?: 'button' | 'submit' | 'reset';
  classNameButtonEnabled?: string;
  classNameButtonDisabled?: string;
  ref?: Ref<HTMLButtonElement | null>;
  onClickHandler?: () => void;
}

/**
 * A simple button component.
 *
 * @param props - Component props.
 * @returns The rendered button element.
 */
export function Button(props: IButtonDTO) {
  const { text, title, disabled, rtl, type, classNameButtonEnabled, classNameButtonDisabled, ref, onClickHandler } = props;

  // Handle key down to mimic click
  const handleKeyDown = useCallback((handler: () => void, e: KeyboardEvent) => {
    if (e.key === Constants.KeyMimicClick) {
      e.preventDefault();
      handler();
    }
  }, []);

  // Interactive props
  const interactiveProps = useMemo(() => {
    if (!onClickHandler) return {};
    const onClick = disabled ? undefined : onClickHandler;
    const onKeyDown = disabled ? undefined : handleKeyDown.bind(null, onClickHandler);
    return {
      tabIndex: 0,
      onClick,
      onKeyDown
    };
  }, [disabled, onClickHandler, handleKeyDown]);

  // Compute class names
  const mainClassName = useMemo(() => (disabled ? Styles.disabled : Styles.enabled), [disabled]);
  const givenClassName = useMemo(() => (disabled ? classNameButtonDisabled : classNameButtonEnabled), [disabled, classNameButtonDisabled, classNameButtonEnabled]);
  const className = useMemo(() => (givenClassName ? `${mainClassName} ${givenClassName}` : mainClassName), [mainClassName, givenClassName]);

  // Render Button element
  const resolvedTitle = useMemo(() => title ?? (rtl && text ? `\u200F${text}` : text), [title, rtl, text]);
  const dir = useMemo(() => (rtl ? 'rtl' : undefined), [rtl]);
  return (
    <button ref={ref} type={type} className={className} dir={dir} title={resolvedTitle} {...interactiveProps}>
      {text}
    </button>
  );
}
